import type { RoleLevel, RoleLevelPreference } from "../types/job";

export const ROLE_LEVEL_STORAGE_KEY = "calijobs-role-level-preference-v1";

const ROLE_LEVEL_STORAGE_KEY_LEGACY = "301-role-level-preference-v1";

export const ROLE_LEVEL_OPTIONS: { value: RoleLevel; label: string; description: string }[] = [
  {
    value: "intern",
    label: "Intern",
    description: "Internships, co-ops, summer programs",
  },
  {
    value: "entry_new_grad",
    label: "Entry / new grad",
    description:
      "Junior, new grad, campus & associate roles — plus unleveled software engineer titles (no senior/staff/lead/II+)",
  },
  {
    value: "senior",
    label: "Mid / Senior",
    description:
      "Titles that say mid/senior/lead, Engineer II+, MTS, etc. — not plain “Software Engineer” without a level",
  },
  {
    value: "principal_staff",
    label: "Principal / staff",
    description: "Principal, staff, distinguished, and similar levels",
  },
];

const VALID_PREFERENCE = new Set<RoleLevelPreference>([
  "all",
  "intern",
  "entry_new_grad",
  "senior",
  "principal_staff",
]);

/** Principal / staff / distinguished IC signals (checked first after intern). */
function hasPrincipalStaffLevel(t: string): boolean {
  return (
    /\bprincipal\b/.test(t) ||
    /\bsr\.?\s*principal\b/.test(t) ||
    /\bstaff\s+(software|engineer|systems|platform|data)\b/.test(t) ||
    /\b(staff|principal)\s+engineer\b/.test(t) ||
    /\bdistinguished\b/.test(t) ||
    /\bfellow\b/.test(t)
  );
}

/**
 * Explicit mid/senior markers (not “plain” software engineer).
 * Used to exclude SE II / lead / senior from the widened Entry filter.
 */
function hasMidOrSeniorLevelMarks(t: string): boolean {
  return (
    /\bmid[\s-]?level\b/.test(t) ||
    /\bmid[\s-]career\b/.test(t) ||
    /\bmid\s+(?:software\s+)?engineer\b/.test(t) ||
    /\bsenior\b/.test(t) ||
    /\bsr\.?\s+(?!principal)/.test(t) ||
    /\bsnr\.?\s+(?!principal)/.test(t) ||
    /\blead\s+(software|engineer|full|backend|frontend|mobile|platform|infra)/.test(t) ||
    /\b(software|engineering)\s+lead\b/.test(t) ||
    /\btech\s+lead\b/.test(t) ||
    /\btechnical\s+leader\b/.test(t) ||
    /\bmember\s+of\s+technical\s+staff\b/.test(t) ||
    /\bsoftware\s+engineer\s+(ii|iii|iv|2|3|4|5|6|7|8|9)\b/.test(t) ||
    /\bengineer\s+(ii|iii|iv)\b/.test(t) ||
    /\bengineer\s+[2-9]\b/.test(t) ||
    /\bswe\s+(ii|iii|iv|2|3|4)\b/.test(t) ||
    /\bdeveloper\s+(ii|iii|iv|2|3)\b/.test(t)
  );
}

function hasExplicitEntryMarks(t: string): boolean {
  return (
    /\bjunior\b/.test(t) ||
    /\bentry[\s-]?level\b/.test(t) ||
    /\bearly\s+career\b/.test(t) ||
    /\bnew\s+grad/.test(t) ||
    /\bgraduate\s+software\b/.test(t) ||
    /\bsoftware\s+engineer\s+i\b/.test(t) ||
    /\bengineer\s+i\b/.test(t) ||
    /\bdeveloper\s+i\b/.test(t) ||
    /\bcampus\b/.test(t) ||
    /\buniversity\s+students?\b/.test(t) ||
    /\bassociate\s+(software|engineer|developer)\b/.test(t) ||
    /\b(software\s+)?engineer\s+associate\b/.test(t) ||
    /\bassociate\s+engineer\b/.test(t)
  );
}

/**
 * Common software IC titles when no explicit level was detected above.
 * Default bucket is **senior** (mid); Entry filter also includes these when no mid/senior marks.
 */
function looksLikeUnleveledSoftwareIc(t: string): boolean {
  return (
    /\bsoftware\s+engineer\b/.test(t) ||
    /\bsoftware\s+developer\b/.test(t) ||
    /\bsoftware\s+development\s+engineer\b/.test(t) ||
    /\bfull[\s-]?stack\s+engineer\b/.test(t) ||
    /\b(backend|frontend|front-?end|mobile|ios|android|platform|web)\s+engineer\b/.test(t) ||
    /\b(site\s+reliability|sre|devops|infrastructure|infra)\s+engineer\b/.test(t) ||
    /\b(data|machine\s+learning|ml|ai)\s+engineer\b/.test(t) ||
    /\b(embedded|firmware|hardware|security|application|applications)\s+engineer\b/.test(t) ||
    /\bengineer,?\s+software\b/.test(t)
  );
}

/**
 * Map a job title to a coarse role bucket. Order matters.
 * Plain “Software Engineer …” (no senior/II+/lead) is stored as **senior** internally so one title maps to one level;
 * `jobMatchesRolePreference` treats those as Entry-only for the Mid/Senior filter (requires `hasMidOrSeniorLevelMarks`).
 */
export function inferJobRoleLevel(title: string): RoleLevel | "other" {
  const t = title.toLowerCase();

  if (/\bintern(ship)?\b|\bco-?op\b|\bsummer\s+intern/.test(t)) {
    return "intern";
  }

  if (hasPrincipalStaffLevel(t)) {
    return "principal_staff";
  }

  if (hasMidOrSeniorLevelMarks(t)) {
    return "senior";
  }

  if (hasExplicitEntryMarks(t)) {
    return "entry_new_grad";
  }

  if (looksLikeUnleveledSoftwareIc(t)) {
    return "senior";
  }

  return "other";
}

export function readRoleLevelPreference(): RoleLevelPreference | null {
  try {
    let raw = localStorage.getItem(ROLE_LEVEL_STORAGE_KEY);
    if (raw === null) {
      raw = localStorage.getItem(ROLE_LEVEL_STORAGE_KEY_LEGACY);
      if (
        raw !== null &&
        VALID_PREFERENCE.has(raw as RoleLevelPreference)
      ) {
        try {
          localStorage.setItem(ROLE_LEVEL_STORAGE_KEY, raw);
          localStorage.removeItem(ROLE_LEVEL_STORAGE_KEY_LEGACY);
        } catch {
          /* ignore */
        }
      }
    }
    if (raw === null) return null;
    if (VALID_PREFERENCE.has(raw as RoleLevelPreference)) {
      return raw as RoleLevelPreference;
    }
    return null;
  } catch {
    return null;
  }
}

export function persistRoleLevelPreference(value: RoleLevelPreference): void {
  try {
    localStorage.setItem(ROLE_LEVEL_STORAGE_KEY, value);
  } catch {
    /* ignore quota / private mode */
  }
}

export function jobMatchesRolePreference(
  title: string,
  preference: RoleLevelPreference,
): boolean {
  if (preference === "all") return true;

  const t = title.toLowerCase();
  const level = inferJobRoleLevel(title);

  if (preference === "entry_new_grad") {
    if (level === "entry_new_grad") return true;
    if (level !== "senior") return false;
    if (hasMidOrSeniorLevelMarks(t)) return false;
    return looksLikeUnleveledSoftwareIc(t);
  }

  if (preference === "senior") {
    if (level !== "senior") return false;
    return hasMidOrSeniorLevelMarks(t);
  }

  return level === preference;
}
